import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  db, collection, query, where, getDocs, onSnapshot,
  doc, updateDoc, serverTimestamp 
} from '../firebase/config';
import { 
  Briefcase, TrendingUp, Clock, Calendar, Users, 
  Plus, Star, DollarSign, Bell, ArrowRight, ArrowUpRight,
  MessageCircle, User, CheckCircle, X, Edit2, Share2,
  Mail, Phone, MapPin, Image, ChevronRight
} from 'lucide-react';

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({
    revenue: 0,
    bookings: 0,
    clients: 0,
    rating: 0,
    todaySales: 0,
    pendingCount: 0,
    confirmedCount: 0,
    completedCount: 0,
  });

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        const servicesQuery = query(
          collection(db, 'services'),
          where('professionalId', '==', user.uid)
        );
        const servicesSnapshot = await getDocs(servicesQuery);
        const servicesData = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setServices(servicesData);
      } catch (error) {
        console.error('Error loading services:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('professionalId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(bookingsQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setBookings(data);

      const totalRevenue = data
        .filter(b => b.status === 'completed' || b.status === 'confirmed')
        .reduce((sum, b) => sum + (b.servicePrice || 0), 0);

      const today = new Date().toISOString().split('T')[0];
      const todaySales = data
        .filter(b => b.date === today && (b.status === 'completed' || b.status === 'confirmed'))
        .reduce((sum, b) => sum + (b.servicePrice || 0), 0);

      const uniqueClients = new Set(data.map(b => b.clientId)).size;

      // Real rating calculation from reviews
      const ratedBookings = data.filter(b => b.rating);
      const avgRating = ratedBookings.length > 0
        ? (ratedBookings.reduce((sum, b) => sum + b.rating, 0) / ratedBookings.length)
        : 0;

      setStats({
        revenue: totalRevenue,
        bookings: data.length,
        clients: uniqueClients,
        rating: avgRating,
        todaySales: todaySales,
        pendingCount: data.filter(b => b.status === 'pending').length,
        confirmedCount: data.filter(b => b.status === 'confirmed').length,
        completedCount: data.filter(b => b.status === 'completed').length,
      });
    }, (error) => {
      console.error('Error loading bookings:', error);
    });

    return () => unsubscribe();
  }, [user]);

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const todayBookings = bookings.filter(b => {
    if (!b.date) return false;
    const today = new Date().toISOString().split('T')[0];
    return b.date === today && (b.status === 'confirmed' || b.status === 'pending');
  });

  const handleAccept = async (bookingId) => {
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'confirmed',
        confirmedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error accepting booking:', error);
      alert('Failed to accept booking');
    }
  };

  const handleDecline = async (bookingId) => {
    if (!window.confirm('Decline this booking?')) return;
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error declining booking:', error);
      alert('Failed to decline booking');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/professional/${user.uid}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: userData?.businessName || userData?.name || 'My Business',
          text: 'Book with me on Skytone!',
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Profile link copied to clipboard!');
    }
  };

  const handleCall = () => {
    if (userData?.phone) {
      window.location.href = `tel:${userData.phone}`;
    } else {
      alert('No phone number on file. Please add one in your profile.');
    }
  };

  const handleEmail = () => {
    if (userData?.email) {
      window.location.href = `mailto:${userData.email}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // SOLID COLOR STAT CARD (no gradients)
  // ============================================================
  const StatCard = ({ bg, icon, title, value, prefix = 'R', onClick }) => (
    <button
      onClick={onClick}
      className={`${bg} rounded-3xl p-5 relative overflow-hidden transition-all hover:scale-[1.02] cursor-pointer shadow-sm text-left w-full`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="bg-white/25 w-7 h-7 rounded-full flex items-center justify-center text-white">
            {icon}
          </div>
          <span className="text-xs font-bold text-white tracking-wide">{title}</span>
        </div>
        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white">
          <ArrowUpRight size={12} />
        </div>
      </div>

      <div className="text-2xl md:text-3xl font-bold text-white">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </button>
  );

  // ============================================================
  // DESKTOP VIEW
  // ============================================================
  const DesktopView = () => (
    <div className="hidden lg:block max-w-7xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-serif font-bold text-gray-900">Business Overview</h1>
        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-50 shadow-sm">
          <div className="w-8 h-8 rounded-full bg-[#CDBCE7] flex items-center justify-center text-white text-xs font-bold">
            {(userData?.businessName || userData?.name || 'P').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-900 leading-tight">
              {userData?.businessName || userData?.name || 'Professional'}
            </p>
            <p className="text-[10px] text-gray-400 leading-tight">
              {userData?.specialization || 'Business Owner'}
            </p>
          </div>
          <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
            Active
          </span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT SIDE - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Business Summary - Real Stats */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#010101] flex items-center justify-center text-white">
                  <Briefcase size={14} />
                </div>
                <h2 className="font-serif font-bold text-gray-900">Business Summary</h2>
              </div>
            </div>

            {/* Stat cards - all clickable, all real data */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                bg="bg-[#E8B48E]"
                icon={<DollarSign size={14} />}
                title="Total Revenue"
                value={stats.revenue}
                onClick={() => navigate('/professional-dashboard/bookings')}
              />
              <StatCard
                bg="bg-[#8BAFD4]"
                icon={<Calendar size={14} />}
                title="Total Bookings"
                value={stats.bookings}
                prefix=""
                onClick={() => navigate('/professional-dashboard/bookings')}
              />
              <StatCard
                bg="bg-[#AF8BD4]"
                icon={<Users size={14} />}
                title="Total Clients"
                value={stats.clients}
                prefix=""
                onClick={() => navigate('/professional-dashboard/bookings')}
              />

              <StatCard
                bg="bg-[#E88B8B]"
                icon={<Clock size={14} />}
                title="Pending"
                value={stats.pendingCount}
                prefix=""
                onClick={() => navigate('/professional-dashboard/bookings')}
              />
              <StatCard
                bg="bg-[#D48BC4]"
                icon={<CheckCircle size={14} />}
                title="Confirmed"
                value={stats.confirmedCount}
                prefix=""
                onClick={() => navigate('/professional-dashboard/bookings')}
              />
              <StatCard
                bg="bg-[#E88BA8]"
                icon={<Star size={14} />}
                title="Rating"
                value={stats.rating > 0 ? stats.rating.toFixed(1) : '0'}
                prefix=""
                onClick={() => navigate('/professional-dashboard/profile')}
              />
            </div>
          </div>

          {/* Action Required - Real Pending Bookings */}
          {pendingBookings.length > 0 && (
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-red-50">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-red-400 flex items-center justify-center text-white">
                    <Bell size={14} />
                  </div>
                  <h2 className="font-serif font-bold text-gray-900">
                    Action Required ({pendingBookings.length})
                  </h2>
                </div>
                <button
                  onClick={() => navigate('/professional-dashboard/bookings')}
                  className="text-sm text-[#CDBCE7] font-medium hover:underline flex items-center gap-1"
                >
                  View All <ArrowRight size={14} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {pendingBookings.slice(0, 2).map((booking) => (
                  <div key={booking.id} className="bg-[#FCF8F9] rounded-2xl p-4 border border-gray-50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#CDBCE7]">
                        <User size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-gray-900 truncate">
                          {booking.clientName || 'Client'}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {booking.serviceName} • {booking.date} at {booking.time}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAccept(booking.id)}
                        className="flex-1 bg-[#010101] text-white text-xs font-bold py-2.5 rounded-xl hover:bg-black/90 transition-colors flex items-center justify-center gap-1"
                      >
                        <CheckCircle size={12} /> Accept
                      </button>
                      <button
                        onClick={() => handleDecline(booking.id)}
                        className="px-4 bg-white text-gray-600 text-xs font-bold py-2.5 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <X size={12} /> Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Today's Schedule - Real Data */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#010101] flex items-center justify-center text-white">
                  <Calendar size={14} />
                </div>
                <h2 className="font-serif font-bold text-gray-900">Today's Schedule</h2>
              </div>
              <button
                onClick={() => navigate('/professional-dashboard/bookings')}
                className="text-sm text-[#CDBCE7] font-medium hover:underline flex items-center gap-1"
              >
                View All <ArrowRight size={14} />
              </button>
            </div>

            {todayBookings.length > 0 ? (
              <div className="space-y-3">
                {todayBookings.slice(0, 4).map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center gap-4 p-3 rounded-2xl bg-[#FCF8F9] border border-gray-50"
                  >
                    <div className="w-16 text-center">
                      <div className="font-bold text-gray-900 text-sm">
                        {booking.time?.split(':')[0] || '10'}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400">
                        {booking.time?.split(':')[1] || '00'}
                      </div>
                    </div>
                    <div className="w-px h-10 bg-gray-100"></div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 text-sm">
                        {booking.clientName || 'Client'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {booking.serviceName || 'Service'}
                      </div>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest border ${
                      booking.status === 'confirmed'
                        ? 'text-emerald-500 bg-emerald-50 border-emerald-100'
                        : 'text-orange-500 bg-orange-50 border-orange-100'
                    }`}>
                      {booking.status}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400 text-sm">No bookings today</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - 1/3 width */}
        <div className="space-y-6">
          
          {/* Profile Card */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/professional-dashboard/profile')}
                  className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Edit Profile"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={handleShare}
                  className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Share Profile"
                >
                  <Share2 size={14} />
                </button>
              </div>
              <button
                onClick={() => navigate('/professional-dashboard/profile')}
                className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="View Full Profile"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="flex flex-col items-center mb-5">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#FCF8F9] shadow-md mb-3">
                {userData?.profileImage ? (
                  <img src={userData.profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#CDBCE7] flex items-center justify-center text-white text-2xl font-bold">
                    {(userData?.businessName || userData?.name || 'P').slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <h3 className="font-serif font-bold text-gray-900 text-center">
                {userData?.businessName || userData?.name || 'Professional'}
              </h3>
              <p className="text-[11px] text-gray-400 text-center mt-1">
                {userData?.specialization || 'Beauty Professional'}
              </p>
            </div>

            {/* Action buttons - all functional */}
            <div className="flex gap-2 justify-center flex-wrap">
              <button
                onClick={() => navigate('/professional-dashboard/profile')}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Edit Profile"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => navigate('/professional-dashboard/inbox')}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Messages"
              >
                <MessageCircle size={14} />
              </button>
              <button
                onClick={handleCall}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Call"
              >
                <Phone size={14} />
              </button>
              <button
                onClick={() => navigate('/professional-dashboard/services')}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Add Service"
              >
                <Plus size={14} />
              </button>
              <button
                onClick={handleEmail}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Email"
              >
                <Mail size={14} />
              </button>
              <button
                onClick={() => navigate('/professional-dashboard/services')}
                className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Services"
              >
                <Image size={14} />
              </button>
            </div>
          </div>

          {/* Detailed Information */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#010101] flex items-center justify-center text-white">
                  <User size={14} />
                </div>
                <h2 className="font-serif font-bold text-gray-900 text-sm">Detailed Information</h2>
              </div>
              <button
                onClick={() => navigate('/professional-dashboard/profile')}
                className="w-7 h-7 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                title="Edit"
              >
                <Edit2 size={12} />
              </button>
            </div>

            <div className="space-y-4">
              {[
                { icon: <User size={14} />, label: 'First Name', value: (userData?.name || '').split(' ')[0] || '—' },
                { icon: <User size={14} />, label: 'Last Name', value: (userData?.name || '').split(' ').slice(1).join(' ') || '—' },
                { icon: <Mail size={14} />, label: 'Email', value: userData?.email || '—' },
                { icon: <Phone size={14} />, label: 'Phone Number', value: userData?.phone || '—' },
                { icon: <MapPin size={14} />, label: 'Location', value: userData?.location?.address || userData?.location || '—' },
                { icon: <Clock size={14} />, label: 'Last Connected', value: new Date().toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) },
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate('/professional-dashboard/profile')}
                  className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 w-full text-left hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-400 shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-medium">{item.label}</p>
                      <p className="text-xs font-bold text-gray-900 truncate max-w-[140px]">{item.value}</p>
                    </div>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-400">
                    <Edit2 size={10} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 rounded-full bg-[#010101] flex items-center justify-center text-white">
                <Plus size={14} />
              </div>
              <h2 className="font-serif font-bold text-gray-900 text-sm">Quick Actions</h2>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => navigate('/professional-dashboard/services')}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FCF8F9] hover:bg-gray-100 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#CDBCE7]">
                  <Plus size={14} />
                </div>
                <span className="text-sm font-bold text-gray-900">Add Service</span>
                <ArrowRight size={14} className="ml-auto text-gray-400" />
              </button>

              <button
                onClick={() => navigate('/professional-dashboard/bookings')}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FCF8F9] hover:bg-gray-100 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#CDBCE7]">
                  <Calendar size={14} />
                </div>
                <span className="text-sm font-bold text-gray-900">View All Bookings</span>
                <ArrowRight size={14} className="ml-auto text-gray-400" />
              </button>

              <button
                onClick={() => navigate('/professional-dashboard/inbox')}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FCF8F9] hover:bg-gray-100 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#CDBCE7]">
                  <MessageCircle size={14} />
                </div>
                <span className="text-sm font-bold text-gray-900">Messages</span>
                <ArrowRight size={14} className="ml-auto text-gray-400" />
              </button>

              <button
                onClick={handleShare}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FCF8F9] hover:bg-gray-100 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#CDBCE7]">
                  <Share2 size={14} />
                </div>
                <span className="text-sm font-bold text-gray-900">Share My Profile</span>
                <ArrowRight size={14} className="ml-auto text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // MOBILE VIEW
  // ============================================================
  const MobileView = () => (
    <div className="lg:hidden h-full overflow-y-auto pb-28 bg-[#FCF8F9] animate-fade-in font-sans relative">
      <div className="absolute top-[-50px] right-[-50px] w-64 h-64 bg-[#CDBCE7]/20 rounded-full blur-3xl opacity-60"></div>

      {/* Header */}
      <div className="px-6 pt-10 pb-6 sticky top-0 bg-[#FCF8F9]/80 backdrop-blur-xl z-20">

        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900 leading-tight">Good morning,</h1>
          <h1 className="text-3xl font-serif font-bold text-[#CDBCE7] leading-tight mt-[-4px]">
            {userData?.businessName || userData?.name || 'Professional'}
          </h1>
        </div>
      </div>

      {/* Stat cards - mobile 2-col */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            bg="bg-[#E8B48E]"
            icon={<DollarSign size={12} />}
            title="Revenue"
            value={stats.revenue}
            onClick={() => navigate('/professional-dashboard/bookings')}
          />
          <StatCard
            bg="bg-[#8BAFD4]"
            icon={<Calendar size={12} />}
            title="Bookings"
            value={stats.bookings}
            prefix=""
            onClick={() => navigate('/professional-dashboard/bookings')}
          />
          <StatCard
            bg="bg-[#AF8BD4]"
            icon={<Users size={12} />}
            title="Clients"
            value={stats.clients}
            prefix=""
            onClick={() => navigate('/professional-dashboard/bookings')}
          />
          <StatCard
            bg="bg-[#E88B8B]"
            icon={<Clock size={12} />}
            title="Pending"
            value={stats.pendingCount}
            prefix=""
            onClick={() => navigate('/professional-dashboard/bookings')}
          />
        </div>
      </div>

      {/* Action Required - Mobile */}
      {pendingBookings.length > 0 && (
        <div className="px-6 mb-8">
          <div className="flex justify-between items-end mb-4">
            <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">
              Action Required
            </h2>
            <button
              onClick={() => navigate('/professional-dashboard/bookings')}
              className="text-xs text-[#CDBCE7] font-medium flex items-center gap-1"
            >
              View All <ArrowRight size={12} />
            </button>
          </div>
          {pendingBookings.slice(0, 1).map((booking) => (
            <div
              key={booking.id}
              className="bg-white rounded-[2rem] p-4 shadow-sm border border-red-50 relative overflow-hidden"
            >
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-400"></div>
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3 pl-2">
                  <div className="w-10 h-10 rounded-full bg-[#FCF8F9] flex items-center justify-center text-[#CDBCE7]">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">
                      {booking.clientName || 'Client'}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {booking.serviceName} • {booking.date} at {booking.time}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pl-2 mt-4">
                <button
                  onClick={() => handleAccept(booking.id)}
                  className="flex-1 bg-[#010101] text-white text-xs font-bold py-2.5 rounded-xl"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleDecline(booking.id)}
                  className="px-4 bg-gray-50 text-gray-600 text-xs font-bold py-2.5 rounded-xl border border-gray-100"
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Today's Schedule - Mobile */}
      <div className="px-6 mb-8">
        <div className="flex justify-between items-end mb-4">
          <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">
            Today's Schedule
          </h2>
          <button
            onClick={() => navigate('/professional-dashboard/bookings')}
            className="text-xs text-[#CDBCE7] font-medium flex items-center gap-1"
          >
            View All <ArrowRight size={12} />
          </button>
        </div>
        <div className="space-y-3">
          {todayBookings.length > 0 ? (
            todayBookings.slice(0, 3).map((booking) => (
              <div
                key={booking.id}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 flex items-center gap-4"
              >
                <div className="w-14 text-center">
                  <div className="font-bold text-gray-900 text-sm">
                    {booking.time?.split(':')[0] || '10'}
                  </div>
                  <div className="text-[10px] font-bold text-gray-400">
                    {booking.time?.split(':')[1] || '00'}
                  </div>
                </div>
                <div className="w-px h-10 bg-gray-100"></div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-[13px]">
                    {booking.clientName || 'Client'}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {booking.serviceName || 'Service'}
                  </div>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest border ${
                  booking.status === 'confirmed'
                    ? 'text-emerald-500 bg-emerald-50 border-emerald-100'
                    : 'text-orange-500 bg-orange-50 border-orange-100'
                }`}>
                  {booking.status}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 bg-white rounded-2xl border border-gray-50">
              <p className="text-gray-400 text-sm">No bookings today</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions - Mobile */}
      <div className="px-6 mb-8">
        <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase mb-4">
          Quick Actions
        </h2>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/professional-dashboard/services')}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white shadow-sm border border-gray-50 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-[#CDBCE7]">
              <Plus size={16} />
            </div>
            <span className="text-sm font-bold text-gray-900">Add Service</span>
            <ArrowRight size={16} className="ml-auto text-gray-400" />
          </button>

          <button
            onClick={() => navigate('/professional-dashboard/bookings')}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white shadow-sm border border-gray-50 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-[#CDBCE7]">
              <Calendar size={16} />
            </div>
            <span className="text-sm font-bold text-gray-900">View All Bookings</span>
            <ArrowRight size={16} className="ml-auto text-gray-400" />
          </button>

          <button
            onClick={handleShare}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white shadow-sm border border-gray-50 hover:shadow-md transition-all"
          >
            <div className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-[#CDBCE7]">
              <Share2 size={16} />
            </div>
            <span className="text-sm font-bold text-gray-900">Share My Profile</span>
            <ArrowRight size={16} className="ml-auto text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <DesktopView />
      <MobileView />
    </>
  );
};

export default ProfessionalDashboard;